import { useRouter } from 'next/router'
import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import { Button } from 'src/components/Button/Button'
import { Container } from 'src/components/Container/Container'
import { Headline } from 'src/components/Headline/Headline'
import { IconLoading } from 'src/components/Icons/IconLoading'
import { COFFEE_MATCH_WRAPPER, COFFEE_MATCH_BACK_BUTTON } from 'src/selectors'

import styles from './CoffeeMatch.module.scss'
import { matches } from './Matches.data'
import { Question } from './Question/Question'
import { questions } from './Question/Questions.data'
import { Steps } from './Steps/Steps'

import type { Choice as ChoiceType } from './Question/Questions.data'

const REDIRECTION_TIMER = 300
const REDIRECTION_APPENDIX = 'referrer=coffee-match'

interface CoffeeMatchProps {
  title: string
}

export const CoffeeMatch = ({ title }: CoffeeMatchProps) => {
  const router = useRouter()
  const nodeRef = useRef(null)
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<string[]>([])
  const [choicesToOffer, setChoicesToOffer] = useState<ChoiceType[]>([])

  const currentStep = currentIndex + 1
  const formattedAnswers = answers.join('/')
  const questionCount = Object.keys(questions).length
  const isLastStep = currentIndex === questionCount

  const changeStep = (choice: string | null = null) => {
    const direction = choice ? 1 : -1

    const choices = !choice ? [...answers.splice(0, answers.length - 1)] : [...answers, choice]

    setAnswers(choices)
    setCurrentIndex(currentIndex + direction)
  }

  useEffect(() => {
    if (currentStep > questionCount) {
      return
    }

    // for given question, we look at each potential answer
    //   -> if this answer does not have eliminatedBy property, it is valid potentialAnswer
    //   -> otherwise we check if any of the previous answers provided by the user are actually eliminating this option
    const potentialAnswers: ChoiceType[] = questions[currentStep].filter((choice) => {
      if (!choice.eliminatedBy) {
        return true
      }

      return !choice.eliminatedBy.map((eliminator) => eliminator.join('/')).includes(formattedAnswers)
    })

    // If we are left with no potential answers, we simply increase current step by one and go to next step.
    if (!potentialAnswers.length) {
      setCurrentIndex(currentIndex + 1)
    }

    setChoicesToOffer(potentialAnswers)
  }, [answers, currentIndex, currentStep, questionCount])

  useEffect(() => {
    // Run side effects for the final step.
    if (isLastStep) {
      setTimeout(() => {
        const productLink = matches[formattedAnswers]
        const queryString = !productLink.includes('?') ? `?${REDIRECTION_APPENDIX}` : `&${REDIRECTION_APPENDIX}`

        router.push(`/${language}/k/${productLink}${queryString}`)
      }, REDIRECTION_TIMER)
    }
  }, [currentIndex, isLastStep, language])

  // * safari (and sometimes chrome) back button workaround
  // * if browser back button was used, reset states
  useEffect(() => {
    window.onpageshow = (event) => {
      if (event.persisted) {
        setChoicesToOffer([])
        setCurrentIndex(0)
        setAnswers([])
      }
    }
  }, [])

  return (
    <Container data-cy={COFFEE_MATCH_WRAPPER} data-question-id={currentStep}>
      <Headline variant="h1" className={styles.title}>
        {title}
      </Headline>

      <Steps steps={4} currentIndex={currentIndex} />

      <SwitchTransition>
        <CSSTransition
          timeout={150}
          key={currentIndex}
          nodeRef={nodeRef}
          classNames={{
            enter: styles.transitionEnter,
            enterActive: styles.transitionEnterActive,
            exit: styles.transitionExit,
            exitActive: styles.transitionExitActive,
          }}
        >
          <div className={styles.transitionContainer} ref={nodeRef}>
            {!isLastStep && <Question step={currentStep} choices={choicesToOffer} onQuestionAnswered={changeStep} />}

            {isLastStep && (
              <div className={styles.loadingWrapper}>
                <IconLoading size={144} stroke={1.6} />
              </div>
            )}

            {currentStep > 1 && !isLastStep && (
              <div className={styles.goBackWrapper}>
                <Button data-cy={COFFEE_MATCH_BACK_BUTTON} onClick={() => changeStep()} variant="secondary">
                  {t('coffeeMatch.back')}
                </Button>
              </div>
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>
    </Container>
  )
}
